/**
 * Codec unicode-words — base-4096, 12 bits por palavra.
 *
 * A saida imita uma lista de palavras gerada por maquina: senha longa,
 * seed phrase, spam. Nao tenta parecer uma frase com sentido — tentar e
 * falhar chama mais atencao do que assumir o formato de algo que ja se ve
 * todo dia. A pontuacao aleatoria so evita que dois textos do mesmo tamanho
 * tenham exatamente a mesma forma.
 */
import { fromSymbols, stripLengthPrefix, toSymbols, withLengthPrefix } from './bitstream.js'
import { randomInt, randomPick } from './random.js'
import { DisguiseError } from './types.js'
import { WORD_ALPHABET } from './word-alphabet.js'

export const WORD_BITS = 12
export const WORD_BASE = 1 << WORD_BITS

const INDEX_BY_WORD = new Map<string, number>(WORD_ALPHABET.map((w, i) => [w, i]))

const JOINERS = [' ', ' ', ' ', ', ', ' - '] as const

export function encodeWords(payload: Uint8Array): string {
  const symbols = toSymbols(withLengthPrefix(payload), WORD_BITS)
  const parts: string[] = []
  let sentenceLength = 4 + randomInt(8)

  for (let i = 0; i < symbols.length; i++) {
    const word = WORD_ALPHABET[symbols[i] ?? 0] ?? ''
    parts.push(
      i === 0 || parts[parts.length - 1]?.endsWith('. ') === true ? capitalize(word) : word,
    )
    sentenceLength -= 1
    if (i === symbols.length - 1) break
    if (sentenceLength === 0) {
      parts.push('. ')
      sentenceLength = 4 + randomInt(8)
    } else {
      parts.push(randomPick(JOINERS))
    }
  }
  return `${parts.join('')}.`
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export function decodeWords(text: string): Uint8Array {
  const symbols: number[] = []
  for (const token of tokenize(text)) {
    const index = INDEX_BY_WORD.get(token)
    if (index === undefined) throw new DisguiseError(`palavra fora do dicionario: ${token}`)
    symbols.push(index)
  }
  if (symbols.length === 0) throw new DisguiseError('texto de palavras vazio')
  return stripLengthPrefix(fromSymbols(symbols, WORD_BITS))
}

/** Minusculas, sem pontuacao — sobrevive a autocorrecao e a copia-e-cola. */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter((t) => t.length > 0)
}

export function looksLikeWords(text: string): boolean {
  const tokens = tokenize(text)
  return tokens.length > 0 && tokens.every((t) => INDEX_BY_WORD.has(t))
}
